import { useState, useEffect, useMemo } from "react";
import Pagination from "@/components/dashboard/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Filter, Trash2, CalendarIcon, X, Plus } from "lucide-react";
import { format, isSameDay } from "date-fns";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Reservation {
  id: string;
  reservation_number: string | null;
  date: string;
  duration: number;
  status: string;
  price: number;
  created_at: string;
  customer: { id: string; name: string; email: string } | null;
  service: { id: string; name: string } | null;
}

const statusColors: Record<string, string> = {
  confirmed: "bg-accent/10 text-accent border-accent/20",
  pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
  completed: "bg-primary/10 text-primary border-primary/20",
  no_show: "bg-destructive/10 text-destructive border-destructive/20",
  awaiting_payment: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  expired: "bg-muted text-muted-foreground border-border",
};

const ReservationsPage = () => {
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(() => localStorage.getItem("res_filter_search") || "");
  const [statusFilter, setStatusFilter] = useState(() => localStorage.getItem("res_filter_status") || "all");
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [dateFilter, setDateFilter] = useState<Date | undefined>(() => {
    const paramDate = searchParams.get("date");
    if (paramDate) return new Date(paramDate + "T00:00:00");
    const saved = localStorage.getItem("res_filter_date");
    return saved ? new Date(saved) : undefined;
  });
  const [deleteTarget, setDeleteTarget] = useState<Reservation | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { localStorage.setItem("res_filter_search", search); }, [search]);
  useEffect(() => { localStorage.setItem("res_filter_status", statusFilter); }, [statusFilter]);
  useEffect(() => {
    if (dateFilter) localStorage.setItem("res_filter_date", dateFilter.toISOString());
    else localStorage.removeItem("res_filter_date");
  }, [dateFilter]);

  const hasActiveFilters = search !== "" || statusFilter !== "all" || !!dateFilter;
  const resetFilters = () => { setSearch(""); setStatusFilter("all"); setDateFilter(undefined); };
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { t } = useLanguage();

  const fetchReservations = async () => {
    if (!profile?.organisation_id) return;
    const { data } = await supabase
      .from("reservations")
      .select("*, customer:customers(id, name, email), service:services(id, name)")
      .eq("organisation_id", profile.organisation_id)
      .order("date", { ascending: false });
    setReservations((data as any) || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchReservations();
  }, [profile?.organisation_id]);

  const filtered = reservations.filter((r) => {
    const customerName = r.customer?.name || "";
    const customerEmail = r.customer?.email || "";
    const serviceName = r.service?.name || "";
    const matchesSearch =
      customerName.toLowerCase().includes(search.toLowerCase()) ||
      r.id.toLowerCase().includes(search.toLowerCase()) ||
      (r.reservation_number || "").toLowerCase().includes(search.toLowerCase()) ||
      serviceName.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || r.status === statusFilter;
    const matchesDate = !dateFilter || isSameDay(new Date(r.date), dateFilter);
    return matchesSearch && matchesStatus && matchesDate;
  });

  const PAGE_SIZE = 20;
  const paginatedFiltered = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, statusFilter, dateFilter]);

  const statusLabelKeys: Record<string, string> = {
    confirmed: "reservations.status_confirmed",
    pending: "reservations.status_pending",
    cancelled: "reservations.status_cancelled",
    completed: "reservations.status_completed",
    no_show: "reservations.status_no_show",
    awaiting_payment: "reservations.status_awaiting_payment",
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error } = await supabase.from("reservations").delete().eq("id", deleteTarget.id);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success(t("reservations.deleted"));
      setReservations((prev) => prev.filter((r) => r.id !== deleteTarget.id));
    }
    setDeleteTarget(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-foreground">{t("reservations.title")}</h1>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">{reservations.length} {t("reservations.total")}</Badge>
          <Button size="sm" onClick={() => navigate("/dashboard/reservations/new")}>
            <Plus className="h-4 w-4 mr-1" />
            New
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder={t("reservations.search")} className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-auto sm:min-w-[120px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("reservations.status_all")}</SelectItem>
            <SelectItem value="confirmed">{t("reservations.status_confirmed")}</SelectItem>
            <SelectItem value="pending">{t("reservations.status_pending")}</SelectItem>
            <SelectItem value="cancelled">{t("reservations.status_cancelled")}</SelectItem>
            <SelectItem value="completed">{t("reservations.status_completed")}</SelectItem>
            <SelectItem value="no_show">{t("reservations.status_no_show")}</SelectItem>
            <SelectItem value="awaiting_payment">{t("reservations.status_awaiting_payment")}</SelectItem>
            <SelectItem value="expired">{t("reservations.status_expired")}</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("w-full sm:w-[180px] justify-start text-left font-normal", !dateFilter && "text-muted-foreground")}>
                <CalendarIcon className="h-4 w-4 mr-2" />
                {dateFilter ? format(dateFilter, "MMM d, yyyy") : t("reservations.filter_date")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={dateFilter} onSelect={setDateFilter} initialFocus className="p-3 pointer-events-auto" />
            </PopoverContent>
          </Popover>
          {dateFilter && (
            <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => setDateFilter(undefined)}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={resetFilters}>
            <X className="h-4 w-4 mr-1" />
            {t("reservations.reset_filters")}
          </Button>
        )}
      </div>

      {reservations.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center">
          <p className="text-muted-foreground">{t("reservations.no_items")}</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-xl border border-border bg-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("reservations.col_customer")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("reservations.col_service")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("reservations.col_datetime")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("reservations.col_status")}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("reservations.col_price")}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("reservations.col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedFiltered.map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => navigate(`/dashboard/reservations/${r.id}`)}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-foreground">{r.customer?.name || "—"}</p>
                      <p className="text-xs text-muted-foreground">{r.reservation_number || ""}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{r.service?.name || "—"}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{format(new Date(r.date), "MMM d, yyyy · HH:mm")}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusColors[r.status] || ""}`}>
                        {t(statusLabelKeys[r.status] || r.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">€{Number(r.price).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="md:hidden space-y-3">
            {paginatedFiltered.map((r) => (
              <div key={r.id} className="rounded-xl border border-border bg-card p-4 shadow-card cursor-pointer active:bg-muted/30" onClick={() => navigate(`/dashboard/reservations/${r.id}`)}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-foreground">{r.customer?.name || "—"}</p>
                    <p className="text-xs text-muted-foreground">{r.reservation_number || ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${statusColors[r.status] || ""}`}>
                      {t(statusLabelKeys[r.status] || r.status)}
                    </span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{r.service?.name || "—"}</span>
                  <span className="font-medium text-foreground">€{Number(r.price).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between mt-1 text-xs text-muted-foreground">
                  <span>{format(new Date(r.date), "MMM d, yyyy · HH:mm")}</span>
                </div>
              </div>
            ))}
           </div>

          <Pagination currentPage={page} totalItems={filtered.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("reservations.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("reservations.delete_desc")} <span className="font-medium text-foreground">{deleteTarget?.customer?.name}</span>{t("reservations.delete_undone")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("reservations.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("reservations.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default ReservationsPage;